import { forwardRef, Inject, Injectable } from '@angular/core';
import { LegacyUibModalService } from '../modals/legacy-uib-modal.service';

import { ApiService }            from '../api/api.service';
import { CompanyService }        from '../companies/company.service';
import { TableService }          from '../table/table.service';
import { VendorPaymentsService } from '../vendor-payments/vendor-payments.service';

import ChangeVendorHtml         from './vendor.change.html';
import LinkedVendorsHtml        from './vendors.linked-vendors.html';
import ManuallyLinkVendorHtml   from './vendor.manually-link.html';
import ResolveVendorMatchesHtml from './vendor.resolve-matches.html';
import VendorQuickAddHtml       from './vendor.quick-add.html';

@Injectable({ providedIn: 'root' })
export class VendorFactoryService {
  vendor: any;

  constructor (
    @Inject(forwardRef(() => ApiService)) private apiService: ApiService,
    @Inject(forwardRef(() => CompanyService))
    private companyService: CompanyService,
    @Inject(forwardRef(() => TableService)) private tableService: TableService,
    @Inject(forwardRef(() => VendorPaymentsService))
    private vendorPaymentsService: VendorPaymentsService,
    private $uibModal: LegacyUibModalService
  ) {}

  create(vendor?: any): VendorFactoryService {
    this.vendor = vendor || {};
    return this;
  }

  setVendor(vendor: any): VendorFactoryService {
    this.vendor = vendor;
    return this;
  }

  change(): Promise<any> {
    // TODO: convert $uibModal.open() to NgbModal pattern
    return this.$uibModal.open({
      controller : 'ChangeVendorLiteCtrl',
      keyboard   : false,
      resolve    : { vendor: () => this.vendor },
      templateUrl: ChangeVendorHtml
    } as any).result;
  }

  deleteLinkedVendor(vendor: any): Promise<any> {
    return this.apiService.get('/vendors/destroy_branch_link', [
      this.vendor.id, vendor.company_id, vendor.id
    ]);
  }

  findMatchingVendors(vendor: any): Promise<any> {
    return this.apiService.get('/branch_matching/find_matches_for_vendor', vendor.id);
  }

  getBiller(): Promise<any> {
    return this.apiService.get('/vendor', this.vendor.biller_id);
  }

  getBranchVendorInvoices(params: any = {}): Promise<any> {
    return this.vendorPaymentsService.getInvoicesForPayment(this.vendor, params, true);
  }

  getLinkedVendors(): Promise<any> {
    return this.apiService.get('/vendors/branch_link_index', this.vendor.id);
  }

  isBranchVendor(vendor = this.vendor): boolean {
    return vendor?.is_branch_vendor;
  }

  isLinkedToHeadOffice(vendor = this.vendor): boolean {
    return !!vendor?.branch_link;
  }

  lookupBranchVendorInvoices(): Promise<any> {
    const tableCacheKey = 'branchVendorInvoicesForPayment';

    // TODO: convert $uibModal.open() to NgbModal pattern
    return this.$uibModal.open({
      controller  : 'VendorInvoicesForPaymentCtrl',
      controllerAs: '$ctrl',
      templateUrl : require('../invoices/invoices.for-payment.html'),
      windowClass : 'x-lg-modal',
      resolve     : {
        invoices: () => {
          const cache = this.tableService.getCacheForTable(tableCacheKey);
          return this.getBranchVendorInvoices({
            direction: cache.sorting.direction,
            field    : cache.sorting.field
          });
        },
        settings: () => ({ isBranchPayment: true, tableCacheKey }),
        vendor  : () => this.vendor
      }
    } as any).result;
  }

  manuallyLinkVendor(): Promise<any> {
    return this.apiService.get('/branches/linked_companies').then(company => {
      // TODO: convert $uibModal.open() to NgbModal pattern
      return this.$uibModal.open({
        controller : 'ManuallyLinkVendorLiteCtrl',
        resolve    : {
          company: () => company,
          vendor : () => this.vendor
        },
        templateUrl: ManuallyLinkVendorHtml,
        windowClass: 'x-lg-modal'
      } as any).result;
    });
  }

  resolveMatches(vendor: any, matches: any): Promise<any> {
    // TODO: convert $uibModal.open() to NgbModal pattern
    return this.$uibModal.open({
      controller : 'ResolveVendorMatchesLiteCtrl',
      keyboard   : false,
      resolve    : {
        vendor : () => vendor,
        matches: () => matches
      },
      templateUrl: ResolveVendorMatchesHtml,
      windowClass: 'x-lg-modal'
    } as any).result.catch(() => vendor);
  }

  save(settings: any = {}): Promise<any> {
    const method = this.vendor.id ? 'patch' : 'post';
    const url    = this.vendor.id ? '/vendor' : '/vendors';

    return this.apiService[method](url, this.vendor, 'vendor', null, settings)
      .then(vendor => {
        if (!this.vendor.id
          && this.companyService.isSharedAccounting()
          && this.companyService.isAccountingSetup()
        ) {
          return this.findMatchingVendors(vendor)
            .then(matches => matches.length
              ? this.resolveMatches(vendor, matches).then(() => vendor)
              : vendor
            );
        }
        return vendor;
      });
  }

  saveLinkedVendor(company: any, linkedVendor: any): Promise<any> {
    return this.apiService.get('/vendors/link_to_branch', [
      this.vendor.id, company.company_id, linkedVendor.id
    ]);
  }

  saveMatch(match: any): Promise<any> {
    return this.apiService.post('/branch_matching/link_vendor_ids', [
      { id: this.vendor.id, accounting_id: match.accounting_id }
    ], 'links');
  }

  searchVendorsInCompany(company: any, filters?: any): Promise<any> {
    const defaultFilters = {
      linked_company_id: company.company_id,
      search_term      : '',
      search_type      : 'starts_with'
    };

    return this.apiService.post('vendors/find_in_branch', { ...defaultFilters, ...filters });
  }

  viewLinkedVendors(): Promise<any> {
    // TODO: convert $uibModal.open() to NgbModal pattern
    return this.$uibModal.open({
      controller : 'LinkedVendorsLiteCtrl',
      keyboard   : false,
      resolve    : {
        links : () => this.getLinkedVendors(),
        vendor: () => this.vendor
      },
      templateUrl: LinkedVendorsHtml,
      windowClass: 'lg-modal'
    } as any).result;
  }

  get(id?: any): Promise<any> {
    if (!id) {
      return Promise.resolve({
        address1      : '',
        address2      : '',
        balance       : '0.00',
        company_name  : '',
        contact1_first: '',
        contact2_first: '',
        contact1_last : '',
        contact2_last : '',
        contact1_phone: '',
        contact2_phone: '',
        country_code  : this.companyService.company?.country_code,
        credit_balance: 0.00,
        email         : '',
        fax           : '',
        id            : '',
        phone         : '',
        postcode      : '',
        state         : '',
        suburb        : '',
        web           : ''
      });
    }

    let vendor: any;
    return this.apiService.get('/vendor', id)
      .then(result => {
        vendor = result;
        return this.apiService.get('/vendor/biller_account_balance', id);
      })
      .then(balance => {
        vendor.biller_balance = balance;
        return this.apiService.get('/vendor/biller_credit_balance', id);
      })
      .then(creditBalance => {
        vendor.biller_credit_balance = creditBalance;
        return vendor;
      });
  }

  quickAdd(): Promise<any> {
    // TODO: convert $uibModal.open() to NgbModal pattern
    return this.$uibModal.open({
      controller : 'VendorQuickAddLiteCtrl',
      templateUrl: VendorQuickAddHtml,
      windowClass: 'md-modal'
    } as any).result;
  }
}
